import { isGameType, type GameType } from '@tv-casino/shared';
import type { HostBoot } from './Host.js';

/**
 * The receiver half of native cast, as pure data. The cast device loads
 * `/receiver` (registered as the Cast Web Receiver app), sits on a waiting
 * screen, and the sender hands it one message over the custom namespace: the
 * TV URL — `/host?code=&display=`, `/tv?table=` or `/tv?casino=` — this screen should
 * present. Parsing that URL here, with no React and no cast SDK, is what
 * keeps Receiver.tsx wiring-thin: the exact same boot `Host` derives from its
 * own address, just arriving by a different door.
 */

/** How long a present URL we are willing to even parse. */
const MAX_URL_LENGTH = 2048;

/**
 * Parses the URL the sender handed over into a Host boot. Room codes travel
 * as-is (the server normalizes); casino codes arrive lowercased from the
 * field and are uppercased exactly like `Host`'s own URL boot does. Any URL
 * without a recognizable pairing is refused — the waiting screen stays up
 * rather than half-booting a table.
 */
export function parsePresentUrl(raw: string): HostBoot | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_URL_LENGTH) return null;
  let url: URL;
  try {
    url = new URL(raw, 'https://receiver.invalid');
  } catch {
    return null;
  }
  const gameParam = url.searchParams.get('game');
  const game: GameType = isGameType(gameParam) ? gameParam : 'blackjack';
  const casino = url.searchParams.get('casino');
  const table = url.searchParams.get('table');
  if (table) {
    return {
      game,
      casino: casino ? casino.toUpperCase() : null,
      table: table.toUpperCase(),
      handoff: null,
    };
  }
  if (casino) return { game, casino: casino.toUpperCase(), table: null, handoff: null };
  const code = url.searchParams.get('code');
  const displayToken = url.searchParams.get('display');
  if (code && displayToken) {
    return { game, casino: null, table: null, handoff: { code, displayToken } };
  }
  return null;
}

/**
 * The message payload the sender sends is `{ url }` inside the custom
 * namespace. Anything else — wrong shape, missing url, a number — is not a
 * present request.
 */
export function presentUrlFromMessage(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const url = (data as { url?: unknown }).url;
  return typeof url === 'string' ? url : null;
}

/** The waiting screen's copy, before the sender says anything. */
export function receiverWaiting(castReady: boolean): { line: string; hint: string } {
  return {
    line: 'Waiting for the table',
    hint: castReady
      ? 'Press Cast on the host page to put the night on this screen.'
      : 'This screen presents the table once a host page casts to it.',
  };
}
